import { useFrame, useThree } from '@react-three/fiber';
import {
    memo,
    useLayoutEffect,
    useMemo,
} from 'react';
import {
    FrontSide,
    Vector2,
    Vector3,
} from 'three';
import type { IUniform } from 'three';
import type {
    ModelViewerPedestalLayout,
    ModelViewerPedestalMaterial,
} from '../Pedestal';
import {
    getStudioPedestalBodyLayout,
    getStudioPedestalGroundShadowOffset,
} from './PedestalGeometry';
import { STUDIO_KEY_LIGHT_POSITION } from './config';

interface Props {
    layout: ModelViewerPedestalLayout
    material: ModelViewerPedestalMaterial
}

const PEDESTAL_SHADOW_VERTEX_SHADER = `
    varying vec2 vUv;

    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const PEDESTAL_SHADOW_FRAGMENT_SHADER = `
    uniform vec2 boxHalfSize;
    uniform float boxCornerRadius;
    uniform vec2 castVector;
    uniform float cylinder;
    uniform float endSoftness;
    uniform float glass;
    uniform float opacityScale;
    uniform vec2 planeSize;
    uniform float radius;
    uniform float startSoftness;
    varying vec2 vUv;

    float boxDistance(vec2 point, vec2 halfSize) {
        vec2 edgeDistance = (
            abs(point)
            - halfSize
            + vec2(boxCornerRadius)
        );
        return length(max(edgeDistance, 0.0))
            + min(max(edgeDistance.x, edgeDistance.y), 0.0)
            - boxCornerRadius;
    }

    float shapeDistance(vec2 point) {
        float cylinderDistance = length(point) - radius;
        float rectangularDistance = boxDistance(point, boxHalfSize);
        return mix(rectangularDistance, cylinderDistance, cylinder);
    }

    void main() {
        vec2 point = (vUv - 0.5) * planeSize;
        float sweptDistance = 100000.0;
        float nearestT = 0.0;

        // Project the pedestal footprint from its base to the key-light cast
        // endpoint. Fixed samples are sufficient because the cast is short
        // relative to either supported pedestal footprint.
        for(int sampleIndex = 0; sampleIndex <= 16; sampleIndex++) {
            float sampleT = float(sampleIndex) / 16.0;
            float sampledDistance = shapeDistance(
                point - castVector * sampleT
            );
            if(sampledDistance < sweptDistance) {
                sweptDistance = sampledDistance;
                nearestT = sampleT;
            }
        }

        float softness = mix(startSoftness, endSoftness, nearestT);
        float sweptMask = 1.0 - smoothstep(
            -softness,
            softness,
            sweptDistance
        );
        float contactMask = 1.0 - smoothstep(
            -startSoftness,
            startSoftness * 1.5,
            shapeDistance(point)
        );
        float castOpacity = mix(0.24, 0.14, nearestT);
        float solidAlpha = max(
            sweptMask * castOpacity,
            contactMask * 0.32
        );
        float footprintDistance = shapeDistance(point);
        float outsideFootprint = smoothstep(
            -startSoftness * 0.1,
            startSoftness * 1.5,
            footprintDistance
        );
        float exteriorContact = (
            1.0 - smoothstep(
                0.0,
                startSoftness * 2.0,
                max(footprintDistance, 0.0)
            )
        ) * step(0.0, footprintDistance) * 0.12;
        float glassAlpha = max(
            sweptMask * castOpacity * outsideFootprint,
            exteriorContact
        );
        float alpha = mix(solidAlpha, glassAlpha, glass) * opacityScale;

        gl_FragColor = vec4(vec3(0.0), alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
    }
`;

/**
 * A cheap projected pedestal shadow aligned with the camera-relative Studio
 * key. Keeping it procedural avoids rendering every product GLB into a second
 * live shadow map and preserves the product-only accumulative-shadow cache.
 */
export default memo(function PedestalGroundShadow({layout, material}:Props) {
    const invalidate = useThree(state => state.invalidate);
    const body = useMemo(() => (
        getStudioPedestalBodyLayout(layout, material)
    ), [layout, material]);
    // Three caches ShaderMaterial uniform references when it compiles the
    // program. Keep this object stable and update its values in place whenever
    // the pedestal layout changes.
    const uniforms = useMemo(() => ({
        boxCornerRadius: {value: 0},
        boxHalfSize: {value: new Vector2()},
        castVector: {value: new Vector2()},
        cylinder: {value: 0},
        endSoftness: {value: 0},
        glass: {value: 0},
        opacityScale: {value: 1},
        planeSize: {value: new Vector2()},
        radius: {value: 0},
        startSoftness: {value: 0},
    } satisfies Record<string, IUniform>), []);
    const cameraDirection = useMemo(() => new Vector3(), []);
    const shadowLayout = useMemo(() => {
        const [keyX, keyY, keyZ] = STUDIO_KEY_LIGHT_POSITION;
        const castScale = layout.height / keyY;
        const baseCastWorldX = -keyX * castScale;
        const baseCastWorldZ = -keyZ * castScale;
        const castLength = Math.hypot(baseCastWorldX, baseCastWorldZ);
        const startSoftness = Math.max(
            Math.min(layout.width, layout.depth) * 0.025,
            layout.height * 0.02,
            0.001,
        );
        const endSoftness = Math.max(
            startSoftness * 3,
            layout.height * 0.22,
        );
        const margin = endSoftness * 2.5;
        // The cast vector rotates with the camera. Give the world-aligned
        // receiver enough room for that vector in any direction so the box
        // contact footprint itself never has to rotate.
        const planeWidth = layout.width + castLength * 2 + margin * 2;
        const planeDepth = layout.depth + castLength * 2 + margin * 2;

        return {
            baseCastWorldX,
            baseCastWorldZ,
            endSoftness,
            planeDepth,
            planeWidth,
            startSoftness,
        };
    }, [
        layout.depth,
        layout.height,
        layout.width,
    ]);

    useLayoutEffect(() => {
        const glassBaseInset = material === 'glass'
            ? body.boxEdgeRadius
            : 0;
        uniforms.boxCornerRadius.value = glassBaseInset;
        uniforms.boxHalfSize.value.set(
            layout.width / 2 - glassBaseInset,
            layout.depth / 2 - glassBaseInset,
        );
        uniforms.cylinder.value = layout.shape === 'cylinder' ? 1 : 0;
        uniforms.endSoftness.value = shadowLayout.endSoftness;
        uniforms.glass.value = material === 'glass' ? 1 : 0;
        uniforms.opacityScale.value = material === 'glass' ? 0.65 : 1;
        uniforms.planeSize.value.set(
            shadowLayout.planeWidth,
            shadowLayout.planeDepth,
        );
        uniforms.radius.value = layout.radius - body.cylinderBevel;
        uniforms.startSoftness.value = shadowLayout.startSoftness;
        invalidate();
    }, [
        invalidate,
        body.boxEdgeRadius,
        body.cylinderBevel,
        layout.depth,
        layout.height,
        layout.radius,
        layout.shape,
        layout.width,
        material,
        shadowLayout.endSoftness,
        shadowLayout.planeDepth,
        shadowLayout.planeWidth,
        shadowLayout.startSoftness,
        uniforms,
    ]);

    useFrame(({camera}) => {
        camera.getWorldDirection(cameraDirection);
        const cameraYaw = Math.atan2(
            -cameraDirection.x,
            -cameraDirection.z,
        );
        const yawCosine = Math.cos(cameraYaw);
        const yawSine = Math.sin(cameraYaw);
        const castWorldX = (
            shadowLayout.baseCastWorldX * yawCosine
            + shadowLayout.baseCastWorldZ * yawSine
        );
        const castWorldZ = (
            -shadowLayout.baseCastWorldX * yawSine
            + shadowLayout.baseCastWorldZ * yawCosine
        );
        const castVector = uniforms.castVector.value;

        // PlaneGeometry's local Y points toward world -Z after its floor
        // rotation, so negate the rotated world-Z cast component.
        castVector.set(castWorldX, -castWorldZ);
    }, -1);

    return <mesh
        position={[
            0,
            layout.floorY + getStudioPedestalGroundShadowOffset(layout),
            0,
        ]}
        renderOrder={2}
        rotation={[-Math.PI / 2, 0, 0]}
        scale={[
            shadowLayout.planeWidth,
            shadowLayout.planeDepth,
            1,
        ]}
    >
        <planeGeometry />
        <shaderMaterial
            depthWrite={false}
            fragmentShader={PEDESTAL_SHADOW_FRAGMENT_SHADER}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
            side={FrontSide}
            toneMapped
            transparent
            uniforms={uniforms}
            vertexShader={PEDESTAL_SHADOW_VERTEX_SHADER}
        />
    </mesh>;
});
